import {
  UnknownDocumentType,
  AdvancedSearchField,
  ArdoqId,
  SortOrder,
  AdvancedSearchResultType,
} from '@ardoq/api-types';
import {
  Table,
  tableOperations,
  type Column,
  type ColumnHeaderIcon,
  type Row,
  type SelectableProps,
  type SortDescriptor,
} from '@ardoq/table';
import { Pagination } from '@ardoq/pagination';
import {
  fieldsToFieldsLiteral,
  findSelectedFields,
  getDefaultFields,
  getFieldValueRenderer,
  getSortByString,
  isFieldSortable,
  prependWithDefaults,
} from './utils';
import { get } from 'lodash';
import { filterUnique } from 'utils/collectionUtil';
import { MappedAdvancedSearchResult } from 'search/resultUtils';
import { Multiselect } from '@ardoq/select';
import { FormWrapper } from '@ardoq/forms';
import { InlineText } from '@ardoq/typography';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';
import { formatNumber, type Locale } from '@ardoq/locale';
import { IconName } from '@ardoq/icons';
import { type ReactNode } from 'react';
import type { PermissionContext } from '@ardoq/access-control';
import { fieldIsConfidential } from 'utils/confidentialFieldUtil';

export type OnSortChange = (
  sortBy: string | undefined,
  sortOrder: SortOrder | undefined
) => void;
export type OnPageSelect = (pageNumber: number) => void;
export type OnFieldSelect = (fieldNames: string[]) => void;

type SearchResultTableSelectable = {
  selectable: true;
  selected: ArdoqId[];
  setSelected: (selected: ArdoqId[]) => void;
};
type SearchResultTableNonSelectable = {
  selectable?: false;
};
export type SelectionProps =
  | SearchResultTableSelectable
  | SearchResultTableNonSelectable;

type SearchResultTableProps = SelectionProps & {
  onPageSelect?: OnPageSelect;
  onFieldSelect?: OnFieldSelect;
  onSortChange?: OnSortChange;
  sortOrder?: SortOrder;
  sortBy?: string;
  perPage: number;
  errorMsg?: string;
  page: number;
  total: number;
  fields: AdvancedSearchField[];
  numberFormatByFieldName: Record<string, string | null>;
  permissionContext: PermissionContext;
  isLoading?: boolean;
  results?: MappedAdvancedSearchResult<UnknownDocumentType>[];
  selectedFieldNames?: string[];
  queryRelevantFieldsNames?: string[];
  /**
   * @deprecated We haven't used Intercom Product Tours since 2022, use proper
   * aria attributes instead.
   */
  intercomTarget?: string;
  selectable?: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
};

type SearchResultCellData = {
  content: ReactNode;
  sortValue: string;
};

type SearchResultRowData = Record<string, SearchResultCellData>;

type SearchResultRecord = MappedAdvancedSearchResult<UnknownDocumentType> &
  Record<string, unknown>;

type CellRendererConfig = {
  field: AdvancedSearchField;
  render: ReturnType<typeof getFieldValueRenderer>;
};

const getResultType = (
  results: MappedAdvancedSearchResult<UnknownDocumentType>[]
): AdvancedSearchResultType | null => (results[0] ? results[0].type : null);

const SearchResultCell = ({ content }: SearchResultCellData): ReactNode => (
  <>{content}</>
);

const getCellSortValue = ({ sortValue }: SearchResultCellData): string =>
  sortValue;

const toSortValue = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value);

const isColumnSortable = (
  field: AdvancedSearchField,
  resultType: AdvancedSearchResultType | null,
  canSort: boolean
): boolean =>
  canSort && resultType !== null && Boolean(isFieldSortable(field, resultType));

const toConfidentialHeaderIcon = (
  i18nLocale: I18nLocale
): ColumnHeaderIcon => ({
  name: IconName.CONTROL,
  tooltip: t(
    i18nLocale,
    'componentOverview:fields.decorators.confidential',
    'Confidential field'
  ),
});

const toColumns = (
  selectedFields: AdvancedSearchField[],
  resultType: AdvancedSearchResultType | null,
  canSort: boolean,
  permissionContext: PermissionContext,
  i18nLocale: I18nLocale
): Column<SearchResultRowData>[] =>
  selectedFields.map(field => ({
    id: field.name,
    title: field.label,
    cellRenderer: SearchResultCell,
    valueGetter: getCellSortValue,
    suppressHeaderContextMenu: true,
    getContextMenuItems: () => ['autoSizeThis', 'autoSizeAll'],
    minWidth: 100,
    suppressSorting: !isColumnSortable(field, resultType, canSort),
    ...(fieldIsConfidential(permissionContext, field) && {
      headerIcon: toConfidentialHeaderIcon(i18nLocale),
    }),
  }));

const toRows = (
  results: MappedAdvancedSearchResult<UnknownDocumentType>[],
  cellConfigs: CellRendererConfig[]
): Row<SearchResultRowData>[] =>
  results.map(result => {
    const record = result as SearchResultRecord;
    return {
      id: result.id,
      data: Object.fromEntries(
        cellConfigs.map(({ field, render }) => {
          const rawValue = get(record, field.name);
          return [
            field.name,
            {
              content: render
                ? render(rawValue, record)
                : (rawValue as ReactNode),
              sortValue: toSortValue(rawValue),
            },
          ];
        })
      ),
    };
  });

const toSortDescriptor = (
  selectedFields: AdvancedSearchField[],
  sortBy: string | undefined,
  sortOrder: SortOrder | undefined
): SortDescriptor | null => {
  if (!sortBy || !sortOrder || sortOrder === SortOrder.DEFAULT) return null;
  const field = selectedFields.find(
    candidate => getSortByString(candidate) === sortBy
  );
  if (!field) return null;
  return {
    column: field.name,
    direction: sortOrder === SortOrder.ASC ? 'asc' : 'desc',
  };
};

const SearchResultTable = (props: SearchResultTableProps) => {
  const { results = [], queryRelevantFieldsNames = [] } = props;

  // I assume all the results are of the same type,
  const resultType = getResultType(results);
  const customFields = fieldsToFieldsLiteral(props.fields);
  const defaultFields = getDefaultFields(resultType, props.i18nLocale);
  const fields = Object.assign({}, defaultFields, customFields);

  const queryRelevantKeys = Object.keys(fields).filter(fieldName =>
    queryRelevantFieldsNames.includes(fieldName)
  );

  // do not allow to have none column selected
  // fallback to default set in that case
  const selectedFieldNames = (props.selectedFieldNames || []).length
    ? props.selectedFieldNames || []
    : [
        'name',
        'typeName',
        'lastUpdated',
        'created',
        ...prependWithDefaults(queryRelevantKeys, Object.keys(customFields)),
      ].filter(filterUnique);

  // setSelectedFieldNames with right order
  const setSelectedFieldNames = (selectedOptions: string[] | null) => {
    if (!selectedOptions) return;
    return (
      props.onFieldSelect &&
      props.onFieldSelect(
        Object.keys(fields).filter(fieldName =>
          selectedOptions.includes(fieldName)
        )
      )
    );
  };

  const selectedFields = findSelectedFields(selectedFieldNames, fields);
  const canSort = Boolean(props.onSortChange);
  const columns = toColumns(
    selectedFields,
    resultType,
    canSort,
    props.permissionContext,
    props.i18nLocale
  );

  const cellConfigs: CellRendererConfig[] = selectedFields.map(field => ({
    field,
    render: getFieldValueRenderer(
      field,
      props.locale,
      props.numberFormatByFieldName,
      props.i18nLocale
    ),
  }));
  const rows = props.errorMsg ? [] : toRows(results, cellConfigs);

  const onSortDescriptorChange = (descriptor: SortDescriptor | null) => {
    const { onSortChange } = props;
    if (!onSortChange) return;
    if (!descriptor) {
      onSortChange(undefined, undefined);
      return;
    }
    const field = selectedFields.find(
      candidate => candidate.name === descriptor.column
    );
    if (!field) return;
    onSortChange(
      getSortByString(field),
      descriptor.direction === 'asc' ? SortOrder.ASC : SortOrder.DESC
    );
  };

  const selectable: SelectableProps | undefined = props.selectable
    ? { mode: 'multiple', ids: props.selected, onChange: props.setSelected }
    : undefined;

  const renderEmptyState = () => (
    <FlexBox justify="center" align="center" padding="large">
      <InlineText
        variant="text1"
        color={props.errorMsg ? 'textError' : 'textSubtle'}
      >
        {props.errorMsg ??
          t(
            props.i18nLocale,
            'search:advancedSearchResults.noResults',
            'No results found'
          )}
      </InlineText>
    </FlexBox>
  );

  return (
    <>
      <FlexBox justify="space-between" paddingBottom="small" align="end">
        <FlexBox>
          <InlineText variant="text1">
            {t(
              props.i18nLocale,
              'search:advancedSearchResults.numberOfResults',
              'Number of results:'
            )}{' '}
          </InlineText>
          <InlineText variant="text1Bold">
            &nbsp;{formatNumber(props.total, 'default', props.locale)}
          </InlineText>
        </FlexBox>
        {!!results.length && (
          <FormWrapper>
            <Multiselect
              label={t(
                props.i18nLocale,
                'search:advancedSearchResults.selectColumns',
                'Select columns'
              )}
              value={selectedFieldNames}
              options={Object.values(fields).map(({ name, label }) => ({
                label,
                value: name,
              }))}
              isClearable={false}
              onValueChange={setSelectedFieldNames}
            />
          </FormWrapper>
        )}
      </FlexBox>
      <Table<SearchResultRowData>
        key={`${resultType}|${selectedFieldNames.join('|')}`}
        aria-label={props.intercomTarget ?? 'Search results'}
        columns={columns}
        rows={rows}
        height={`${tableOperations.getDynamicTableHeight(
          rows.length,
          props.perPage,
          { includeBottomScrollbarHeight: true }
        )}px`}
        loading={props.isLoading}
        sortable={{
          descriptor: toSortDescriptor(
            selectedFields,
            props.sortBy,
            props.sortOrder
          ),
          onChange: onSortDescriptorChange,
          externallySorted: true,
        }}
        selectable={selectable}
        renderEmptyState={renderEmptyState}
      />
      <Pagination
        style={{
          padding: '32px 0 0',
          justifyContent: 'center',
        }}
        isDisabled={props.isLoading}
        currentPageNumber={props.page}
        perPage={props.perPage}
        // back-end doesn't support pagination past the 10000th result
        totalResults={Math.min(10000, props.total)}
        onPageSelect={props.onPageSelect}
      />
    </>
  );
};

export { SearchResultTable };
